import type { Id } from '../types';
import type { ProductPos, StoreModel } from './model';

export type Positions = Map<Id, ProductPos>;

/** Pozycje bliższe niż ten próg uznajemy za zlepione i numerujemy model od nowa. */
export const MIN_SEPARATION = 1e-9;

/** Porównanie po jednostkach kodowych — niezależne od locale, więc deterministyczne. */
export function compareIds(a: Id, b: Id): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function sortedPositions(values: Iterable<ProductPos>): Float64Array {
  const all = Array.from(values, (p) => p.pos);
  return Float64Array.from(all).sort();
}

/** Mediana odstępów między kolejnymi pozycjami (pomija remisy); 1, gdy nie ma z czego liczyć. */
export function medianGapOf(values: Iterable<ProductPos>): number {
  const sorted = sortedPositions(values);
  const diffs: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const d = sorted[i]! - sorted[i - 1]!;
    if (d > 0) diffs.push(d);
  }
  if (diffs.length === 0) return 1;
  diffs.sort((a, b) => a - b);
  const mid = diffs.length >> 1;
  return diffs.length % 2 === 1 ? diffs[mid]! : (diffs[mid - 1]! + diffs[mid]!) / 2;
}

export function maxPosOf(values: Iterable<ProductPos>): number {
  let max = -Infinity;
  for (const p of values) if (p.pos > max) max = p.pos;
  return max;
}

/** Pozycje bez zgadniętych — tylko one wyznaczają skalę modelu (odstępy, koniec trasy). */
export function withoutGuessed(values: Iterable<ProductPos>): ProductPos[] {
  return Array.from(values).filter((p) => !p.guessed);
}

/** Czy któraś pozycja zlewa się z podaną. */
export function collides(byProduct: Positions, pos: number): boolean {
  for (const p of byProduct.values()) if (Math.abs(p.pos - pos) < MIN_SEPARATION) return true;
  return false;
}

/** Przenumerowanie na 0,1,2,… z zachowaniem kolejności; remisy rozstrzyga productId. */
export function renumber(byProduct: Positions): void {
  const entries = [...byProduct].sort(([ia, a], [ib, b]) => a.pos - b.pos || compareIds(ia, ib));
  entries.forEach(([, p], i) => {
    p.pos = i;
  });
}

/** Pilnuje, by różne pozycje nie zbliżyły się poniżej precyzji (np. po wielu połowieniach przy przeciąganiu). */
export function keepSeparated(byProduct: Positions): void {
  const sorted = sortedPositions(byProduct.values());
  for (let i = 1; i < sorted.length; i++) {
    const d = sorted[i]! - sorted[i - 1]!;
    if (d > 0 && d < MIN_SEPARATION) {
      renumber(byProduct);
      return;
    }
  }
}

export function medianGap(model: StoreModel): number {
  return medianGapOf(Object.values(model.byProduct));
}

/** Miejsce produktu (od 0) w wyuczonej kolejności sklepu albo null, gdy produkt nie jest znany. */
export function rankOf(model: StoreModel, productId: Id): number | null {
  if (!Object.hasOwn(model.byProduct, productId)) return null;
  const self = model.byProduct[productId]!;
  let rank = 0;
  for (const [id, p] of Object.entries(model.byProduct)) {
    if (p.pos < self.pos || (p.pos === self.pos && compareIds(id, productId) < 0)) rank++;
  }
  return rank;
}

/** Domyślny rozmiar kopii trasy — jedno zdarzenie seeded mieści się w limicie 20 000 znaków. */
export const SNAPSHOT_SIZE = 300;

/**
 * Migawka trasy do zdarzenia seeded: tylko pozycje potwierdzone (bez skopiowanych i zgadniętych),
 * `limit` najpewniejszych (największe n), jako rangi 0..k-1 w kolejności trasy.
 */
export function snapshotOf(model: StoreModel, limit = SNAPSHOT_SIZE): [Id, number][] {
  const best = Object.entries(model.byProduct)
    .filter(([, p]) => !p.seeded && !p.guessed)
    .sort(([ia, a], [ib, b]) => b.n - a.n || compareIds(ia, ib))
    .slice(0, Math.max(0, limit));
  best.sort(([ia, a], [ib, b]) => a.pos - b.pos || compareIds(ia, ib));
  return best.map(([id], rank) => [id, rank]);
}
